import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { archiveRequestAPI } from '../api/api';
import ColumnControl from '../components/Common/ColumnControl';
import './ArchivedFiles.css';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { CASE_FILE_STATUS_LABELS, DAVA_TURU_LABELS, davaTuruEtiketi, getSortedDavaTuruEntries } from '../utils/constants';
import { formatDateForDisplay } from '../utils/dateUtils';

const ArchivedFiles = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [archivedFiles, setArchivedFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: '',
    davaTuru: ''
  });

  // Column visibility control
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('archivedFiles_visibleColumns');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return {};
      }
    }
    // Default: all columns visible
    return {
      foyNo: true,
      arsivNo: true,
      muvekkil: true,
      sorumlu: true,
      karsiSigorta: true,
      karsiTaraf1: true,
      karsiTaraf2: true,
      muvekkilTelefon: false,
      muvekkilIBAN: false,
      sigortaBasvuruTarihi: false,
      davaAcilisTarihi: false,
      sozlesmeOrani: false,
      mahkemeEsas: false,
      muvekkilPlaka: true,
      karsiTarafPlaka: true,
      kazaSehri: true,
      kazaTarihi: true,
      dosyaGelisTarihi: true,
      davaTuru: true,
      tahkimBasvuruTarihi: true,
      referans: true,
      raporTarihi: true,
      zamanAsimiTarihi: true,
      durusmaTarihi: true,
      status: true,
      karsiTarafVekili: true,
      icraDosyaNo: true,
      savcilikCezaDosyaNo: true,
      mahkemaBilgisi: true,
      sulhDurumu: true,
      ibraAsamasi: true,
      gelenOdeme: true,
      gidenOdeme: true,
      smm: true,
      sonIslemYapan: true,
      createdDate: true,
      updatedDate: true
    };
  });

  const archivedFileColumns = [
    { key: 'foyNo', label: 'Foy No' },
    { key: 'arsivNo', label: 'Arşiv No' },
    { key: 'muvekkil', label: 'Müvekkil' },
    { key: 'sorumlu', label: 'Sorumlu' },
    { key: 'karsiSigorta', label: 'Karşı Sigorta' },
    { key: 'karsiTaraf1', label: 'Karşı Taraf 1' },
    { key: 'karsiTaraf2', label: 'Karşı Taraf 2' },
    { key: 'muvekkilTelefon', label: 'Müvekkil Telefon' },
    { key: 'muvekkilIBAN', label: 'Müvekkil IBAN' },
    { key: 'sigortaBasvuruTarihi', label: 'Sigorta Başvuru Tarihi' },
    { key: 'davaAcilisTarihi', label: 'Dava Açılış Tarihi' },
    { key: 'sozlesmeOrani', label: 'Sözleşme Oranı' },
    { key: 'mahkemeEsas', label: 'Mahkeme Esas' },
    { key: 'muvekkilPlaka', label: 'Müvekkil Plaka' },
    { key: 'karsiTarafPlaka', label: 'Karşı Taraf Plaka' },
    { key: 'kazaSehri', label: 'Kaza Şehri' },
    { key: 'kazaTarihi', label: 'Kaza Tarihi' },
    { key: 'dosyaGelisTarihi', label: 'Dosya Geliş Tarihi' },
    { key: 'davaTuru', label: 'Dava Türü' },
    { key: 'tahkimBasvuruTarihi', label: 'Tahkim Başvuru Tarihi' },
    { key: 'referans', label: 'Referans' },
    { key: 'raporTarihi', label: 'Rapor Tarihi' },
    { key: 'zamanAsimiTarihi', label: 'Zaman Aşımı Tarihi' },
    { key: 'durusmaTarihi', label: 'Duruşma Tarihi ve Saati' },
    { key: 'status', label: 'Durum' },
    { key: 'karsiTarafVekili', label: 'Karşı Taraf Vekili' },
    { key: 'icraDosyaNo', label: 'İcra Dosya No' },
    { key: 'savcilikCezaDosyaNo', label: 'Savcılık/Ceza Dosya No' },
    { key: 'mahkemaBilgisi', label: 'Mahkeme Bilgisi' },
    { key: 'sulhDurumu', label: 'Sulh Durumu' },
    { key: 'ibraAsamasi', label: 'İbra Aşaması' },
    { key: 'gelenOdeme', label: 'Gelen Ödeme' },
    { key: 'gidenOdeme', label: 'Giden Ödeme' },
    { key: 'smm', label: 'SMM' },
    { key: 'sonIslemYapan', label: 'Son İşlem Yapan' },
    { key: 'createdDate', label: 'Oluşturma Tarihi' },
    { key: 'updatedDate', label: 'Güncelleme Tarihi' }
  ];

  const handleToggleColumn = (columnKey) => {
    setVisibleColumns(prev => {
      const newState = { ...prev, [columnKey]: !prev[columnKey] };
      localStorage.setItem('archivedFiles_visibleColumns', JSON.stringify(newState));
      return newState;
    });
  };

  useEffect(() => {
    if (token) {
      loadArchivedFiles();
    }
  }, [token]);

  useEffect(() => {
    applyFilters();
  }, [archivedFiles, filters]);

  const loadArchivedFiles = async () => {
    setLoading(true);
    try {
      const data = await archiveRequestAPI.getArchivedFiles();
      setArchivedFiles(data);
      setFilteredFiles(data);
    } catch (error) {
      console.error('Error loading archived files:', error);
      alert('Arşivlenmiş dosyalar yüklenirken hata oluştu: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...archivedFiles];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(file => {
        const muvekkil = (file.Muvekkil ?? file.muvekkil ?? '').toLowerCase();
        const foyNo = String(file.FoyNo ?? file.foyNo ?? '');
        const arsivNo = String(file.ArsivNo ?? file.arsivNo ?? '');
        return muvekkil.includes(searchLower) || 
               foyNo.includes(searchLower) || 
               arsivNo.includes(searchLower);
      });
    }

    if (filters.davaTuru) {
      filtered = filtered.filter(file => {
        const davaTuru = file.DavaTuru ?? file.davaTuru;
        return String(davaTuru) === filters.davaTuru;
      });
    }

    setFilteredFiles(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Export to PDF
  const exportToPDF = () => {
    try {
      const visibleCols = archivedFileColumns.filter(col => visibleColumns[col.key] === true);
      
      if (visibleCols.length === 0) {
        alert('PDF oluşturmak için en az bir sütun görünür olmalıdır.');
        return;
      }
      
      const doc = new jsPDF('l', 'mm', 'a4');
      doc.setFontSize(16);
      doc.text('Arşivlenmiş Dosyalar Listesi', 14, 15);
      
      const headers = visibleCols.map(col => col.label);
      
      const tableData = filteredFiles.map(file => {
        const row = [];
        visibleCols.forEach(col => {
          let value = '';
          switch (col.key) {
            case 'foyNo':
              value = String(file.FoyNo ?? file.foyNo ?? '');
              break;
            case 'arsivNo':
              value = String(file.ArsivNo ?? file.arsivNo ?? '');
              break;
            case 'muvekkil':
              value = String(file.Muvekkil ?? file.muvekkil ?? '').substring(0, 30);
              break;
            case 'sorumlu':
              value = String(file.SorumluAdi ?? file.sorumluAdi ?? '-').substring(0, 25);
              break;
            case 'karsiSigorta':
              value = String(file.KarsiTarafSigortaSirketi ?? file.karsiTarafSigortaSirketi ?? file.KarsiSigorta ?? file.karsiSigorta ?? '').substring(0, 25);
              break;
            case 'karsiTaraf1':
              value = String(file.KarsiTaraf1 ?? file.karsiTaraf1 ?? '').substring(0, 25);
              break;
            case 'karsiTaraf2':
              value = String(file.KarsiTaraf2 ?? file.karsiTaraf2 ?? '').substring(0, 25);
              break;
            case 'muvekkilTelefon':
              value = String(file.MuvekkilTelefon ?? file.muvekkilTelefon ?? '');
              break;
            case 'muvekkilIBAN':
              value = String(file.MuvekkilIBAN ?? file.muvekkilIBAN ?? '');
              break;
            case 'sigortaBasvuruTarihi':
              value = file.SigortaBasvuruTarihi ? formatDate(file.SigortaBasvuruTarihi ?? file.sigortaBasvuruTarihi) : '';
              break;
            case 'davaAcilisTarihi':
              value = file.DavaAcilisTarihi ? formatDate(file.DavaAcilisTarihi ?? file.davaAcilisTarihi) : '';
              break;
            case 'sozlesmeOrani':
              value = String(file.SozlesmeOrani ?? file.sozlesmeOrani ?? '');
              break;
            case 'mahkemeEsas':
              value = String(file.MahkemeEsas ?? file.mahkemeEsas ?? '');
              break;
            case 'muvekkilPlaka':
              value = String(file.MuvekkilPlaka ?? file.muvekkilPlaka ?? '-');
              break;
            case 'karsiTarafPlaka':
              value = String(file.KarsiTarafPlaka ?? file.karsiTarafPlaka ?? '-');
              break;
            case 'kazaSehri':
              value = String(file.KazaSehri ?? file.kazaSehri ?? '-');
              break;
            case 'kazaTarihi':
              value = formatDate(file.KazaTarihi ?? file.kazaTarihi);
              break;
            case 'dosyaGelisTarihi':
              value = formatDate(file.DosyaGelisTarihi ?? file.dosyaGelisTarihi);
              break;
            case 'davaTuru':
              value = davaTuruEtiketi(file) ||  '';
              break;
            case 'tahkimBasvuruTarihi':
              value = formatDate(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi);
              break;
            case 'referans':
              value = String(file.Referans ?? file.referans ?? '-');
              break;
            case 'raporTarihi':
              value = formatDate(file.RaporTarihi ?? file.raporTarihi);
              break;
            case 'zamanAsimiTarihi':
              value = formatDate(file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi);
              break;
            case 'durusmaTarihi':
              value = file.DurusmaTarihi ?? file.durusmaTarihi ? formatDateForDisplay(file.DurusmaTarihi ?? file.durusmaTarihi) : '';
              break;
            case 'status':
              value = CASE_FILE_STATUS_LABELS[file.Status ?? file.status] ?? '';
              break;
            case 'icraDosyaNo':
              value = String(file.IcraDosyaNo ?? file.icraDosyaNo ?? '-');
              break;
            case 'savcilikCezaDosyaNo':
              value = String(file.SavcilikCezaDosyaNo ?? file.savcilikCezaDosyaNo ?? '-');
              break;
            case 'mahkemaBilgisi':
              value = String(file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? '-').substring(0, 30);
              break;
            case 'sulhDurumu':
              value = String(file.SulhDurumu ?? file.sulhDurumu ?? '-');
              break;
            case 'ibraAsamasi':
              value = String(file.IbraAsamasi ?? file.ibraAsamasi ?? '-');
              break;
            case 'gelenOdeme':
              value = String(file.GelenOdemeDurumu ?? file.gelenOdemeDurumu ?? '-');
              break;
            case 'gidenOdeme':
              value = String(file.GidenOdemeDurumu ?? file.gidenOdemeDurumu ?? '-');
              break;
            case 'smm':
              value = String(file.SMM ?? file.smm ?? '-');
              break;
            case 'karsiTarafVekili':
              value = String(file.KarsiTarafSigortaSirketiVekili ?? file.karsiTarafSigortaSirketiVekili ?? '-');
              break;
            case 'sonIslemYapan':
              value = String(file.SonIslemYapanAdi ?? file.sonIslemYapanAdi ?? '-');
              break;
            case 'createdDate':
              value = formatDate(file.CreatedDate ?? file.createdDate);
              break;
            case 'updatedDate':
              value = formatDate(file.UpdatedDate ?? file.updatedDate);
              break;
            default:
              value = '-';
          }
          row.push(value);
        });
        return row;
      });

      autoTable(doc, {
        head: [headers],
        body: tableData,
        startY: 25,
        styles: { fontSize: 8 },
        headStyles: { fillColor: [66, 139, 202], textColor: 255 },
        margin: { top: 25 }
      });

      doc.save('arsivlenmis-dosyalar.pdf');
    } catch (error) {
      console.error('PDF export error:', error);
      alert('PDF oluşturulurken hata oluştu');
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      const visibleCols = archivedFileColumns.filter(col => visibleColumns[col.key] === true);
      const headers = visibleCols.map(col => col.label);
      const worksheetData = [headers];

      filteredFiles.forEach(file => {
        const row = [];
        visibleCols.forEach(col => {
          let value = '';
          switch (col.key) {
            case 'foyNo':
              value = file.FoyNo ?? file.foyNo ?? '';
              break;
            case 'arsivNo':
              value = file.ArsivNo ?? file.arsivNo ?? '';
              break;
            case 'muvekkil':
              value = file.Muvekkil ?? file.muvekkil ?? '';
              break;
            case 'sorumlu':
              value = file.SorumluAdi ?? file.sorumluAdi ?? '-';
              break;
            case 'karsiSigorta':
              value = file.KarsiTarafSigortaSirketi ?? file.karsiTarafSigortaSirketi ?? file.KarsiSigorta ?? file.karsiSigorta ?? '';
              break;
            case 'karsiTaraf1':
              value = file.KarsiTaraf1 ?? file.karsiTaraf1 ?? '';
              break;
            case 'karsiTaraf2':
              value = file.KarsiTaraf2 ?? file.karsiTaraf2 ?? '';
              break;
            case 'muvekkilTelefon':
              value = file.MuvekkilTelefon ?? file.muvekkilTelefon ?? '';
              break;
            case 'muvekkilIBAN':
              value = file.MuvekkilIBAN ?? file.muvekkilIBAN ?? '';
              break;
            case 'sigortaBasvuruTarihi':
              value = file.SigortaBasvuruTarihi ? formatDate(file.SigortaBasvuruTarihi ?? file.sigortaBasvuruTarihi) : '';
              break;
            case 'davaAcilisTarihi':
              value = file.DavaAcilisTarihi ? formatDate(file.DavaAcilisTarihi ?? file.davaAcilisTarihi) : '';
              break;
            case 'sozlesmeOrani':
              value = file.SozlesmeOrani ?? file.sozlesmeOrani ?? '';
              break;
            case 'mahkemeEsas':
              value = file.MahkemeEsas ?? file.mahkemeEsas ?? '';
              break;
            case 'muvekkilPlaka':
              value = file.MuvekkilPlaka ?? file.muvekkilPlaka ?? '-';
              break;
            case 'karsiTarafPlaka':
              value = file.KarsiTarafPlaka ?? file.karsiTarafPlaka ?? '-';
              break;
            case 'kazaSehri':
              value = file.KazaSehri ?? file.kazaSehri ?? '-';
              break;
            case 'kazaTarihi':
              value = file.KazaTarihi ? new Date(file.KazaTarihi ?? file.kazaTarihi).toLocaleDateString('tr-TR') : '';
              break;
            case 'dosyaGelisTarihi':
              value = file.DosyaGelisTarihi ? new Date(file.DosyaGelisTarihi ?? file.dosyaGelisTarihi).toLocaleDateString('tr-TR') : '';
              break;
            case 'davaTuru':
              value = davaTuruEtiketi(file) ||  '';
              break;
            case 'tahkimBasvuruTarihi':
              value = file.TahkimBasvuruTarihi ? new Date(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi).toLocaleDateString('tr-TR') : '';
              break;
            case 'referans':
              value = file.Referans ?? file.referans ?? '-';
              break;
            case 'raporTarihi':
              value = file.RaporTarihi ? new Date(file.RaporTarihi ?? file.raporTarihi).toLocaleDateString('tr-TR') : '';
              break;
            case 'zamanAsimiTarihi':
              value = file.ZamanAsimiTarihi ? new Date(file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi).toLocaleDateString('tr-TR') : '';
              break;
            case 'durusmaTarihi':
              value = file.DurusmaTarihi ?? file.durusmaTarihi ? formatDateForDisplay(file.DurusmaTarihi ?? file.durusmaTarihi) : '';
              break;
            case 'status':
              value = CASE_FILE_STATUS_LABELS[file.Status ?? file.status] ?? '';
              break;
            case 'hasarDosyaNo':
              value = file.HasarDosyaNo ?? file.hasarDosyaNo ?? '-';
              break;
            case 'icraDosyaNo':
              value = file.IcraDosyaNo ?? file.icraDosyaNo ?? '-';
              break;
            case 'savcilikCezaDosyaNo':
              value = file.SavcilikCezaDosyaNo ?? file.savcilikCezaDosyaNo ?? '-';
              break;
            case 'mahkemaBilgisi':
              value = file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? '-';
              break;
            case 'sulhDurumu':
              value = file.SulhDurumu ?? file.sulhDurumu ?? '-';
              break;
            case 'ibraAsamasi':
              value = file.IbraAsamasi ?? file.ibraAsamasi ?? '-';
              break;
            case 'gelenOdeme':
              value = file.GelenOdemeDurumu ?? file.gelenOdemeDurumu ?? '-';
              break;
            case 'gidenOdeme':
              value = file.GidenOdemeDurumu ?? file.gidenOdemeDurumu ?? '-';
              break;
            case 'smm':
              value = file.SMM ?? file.smm ?? '-';
              break;
            case 'karsiTarafVekili':
              value = file.KarsiTarafSigortaSirketiVekili ?? file.karsiTarafSigortaSirketiVekili ?? '-';
              break;
            case 'sonIslemYapan':
              value = file.SonIslemYapanAdi ?? file.sonIslemYapanAdi ?? '-';
              break;
            case 'createdDate':
              value = file.CreatedDate ? new Date(file.CreatedDate ?? file.createdDate).toLocaleDateString('tr-TR') : '';
              break;
            case 'updatedDate':
              value = file.UpdatedDate ? new Date(file.UpdatedDate ?? file.updatedDate).toLocaleDateString('tr-TR') : '';
              break;
            default:
              value = '-';
          }
          row.push(value);
        });
        worksheetData.push(row);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Arşivlenmiş Dosyalar');
      XLSX.writeFile(workbook, 'arsivlenmis-dosyalar.xlsx');
    } catch (error) {
      console.error('Excel export error:', error);
      alert('Excel oluşturulurken hata oluştu');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredFiles.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage);

  const formatDate = (date) => {
    if (!date) return '';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('tr-TR');
    } catch (e) {
      return date;
    }
  };

  return (
    <div className="archived-files-page">
      <div className="page-header">
        <h1>Arşivlenmiş Dosyalar</h1>
      </div>

      {/* Filter Panel */}
      <div className="filter-panel">
        <div className="filter-panel-left">
          <input
            type="text"
            placeholder="Ara (Foy No, Arşiv No, Müvekkil)..."
            className="form-control"
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
          <select
            className="form-control"
            value={filters.davaTuru}
            onChange={(e) => handleFilterChange('davaTuru', e.target.value)}
          >
            <option value="">Tüm Dava Türleri</option>
            {getSortedDavaTuruEntries().map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>
        <div className="filter-panel-right">
          <ColumnControl
            columns={archivedFileColumns}
            visibleColumns={visibleColumns}
            onToggleColumn={handleToggleColumn}
          />
          <select 
            className="form-control"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(Number(e.target.value))}
            style={{ minWidth: '80px' }}
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading-state">Yükleniyor...</div>
      ) : (
        <>
          <div className="table-container">
            <table className="archived-files-table">
              <thead>
                <tr>
                  {visibleColumns.foyNo === true && <th>Foy No</th>}
                  {visibleColumns.arsivNo === true && <th>Arşiv No</th>}
                  {visibleColumns.muvekkil === true && <th>Müvekkil</th>}
                  {visibleColumns.sorumlu === true && <th>Sorumlu</th>}
                  {visibleColumns.karsiSigorta === true && <th>Karşı Sigorta</th>}
                  {visibleColumns.karsiTaraf1 === true && <th>Karşı Taraf 1</th>}
                  {visibleColumns.karsiTaraf2 === true && <th>Karşı Taraf 2</th>}
                  {visibleColumns.muvekkilTelefon === true && <th>Müvekkil Telefon</th>}
                  {visibleColumns.muvekkilIBAN === true && <th>Müvekkil IBAN</th>}
                  {visibleColumns.sigortaBasvuruTarihi === true && <th>Sigorta Başvuru Tarihi</th>}
                  {visibleColumns.davaAcilisTarihi === true && <th>Dava Açılış Tarihi</th>}
                  {visibleColumns.sozlesmeOrani === true && <th>Sözleşme Oranı</th>}
                  {visibleColumns.mahkemeEsas === true && <th>Mahkeme Esas</th>}
                  {visibleColumns.muvekkilPlaka === true && <th>Müvekkil Plaka</th>}
                  {visibleColumns.karsiTarafPlaka === true && <th>Karşı Taraf Plaka</th>}
                  {visibleColumns.kazaSehri === true && <th>Kaza Şehri</th>}
                  {visibleColumns.kazaTarihi === true && <th>Kaza Tarihi</th>}
                  {visibleColumns.dosyaGelisTarihi === true && <th>Dosya Geliş Tarihi</th>}
                  {visibleColumns.davaTuru === true && <th>Dava Türü</th>}
                  {visibleColumns.tahkimBasvuruTarihi === true && <th>Tahkim Başvuru Tarihi</th>}
                  {visibleColumns.referans === true && <th>Referans</th>}
                  {visibleColumns.raporTarihi === true && <th>Rapor Tarihi</th>}
                  {visibleColumns.zamanAsimiTarihi === true && <th>Zaman Aşımı Tarihi</th>}
                  {visibleColumns.durusmaTarihi === true && <th>Duruşma Tarihi ve Saati</th>}
                  {visibleColumns.status === true && <th>Durum</th>}
                  {visibleColumns.icraDosyaNo === true && <th>İcra Dosya No</th>}
                  {visibleColumns.savcilikCezaDosyaNo === true && <th>Savcılık/Ceza Dosya No</th>}
                  {visibleColumns.mahkemaBilgisi === true && <th>Mahkeme Bilgisi</th>}
                  {visibleColumns.sulhDurumu === true && <th>Sulh Durumu</th>}
                  {visibleColumns.ibraAsamasi === true && <th>İbra Aşaması</th>}
                  {visibleColumns.gelenOdeme === true && <th>Gelen Ödeme</th>}
                  {visibleColumns.gidenOdeme === true && <th>Giden Ödeme</th>}
                  {visibleColumns.smm === true && <th>SMM</th>}
                  {visibleColumns.karsiTarafVekili === true && <th>Karşı Taraf Vekili</th>}
                  {visibleColumns.sonIslemYapan === true && <th>Son İşlem Yapan</th>}
                  {visibleColumns.createdDate === true && <th>Oluşturma Tarihi</th>}
                  {visibleColumns.updatedDate === true && <th>Güncelleme Tarihi</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(visibleColumns).filter(v => v === true).length} className="empty-table">
                      Henüz arşivlenmiş dosya bulunmamaktadır.
                    </td>
                  </tr>
                ) : (
                  currentItems.map((file) => {
                    const davaTuru = file.DavaTuru ?? file.davaTuru;
                    const status = file.Status ?? file.status;
                    const fileId = file.Id ?? file.id;
                    return (
                      <tr 
                        key={fileId}
                        className="archived-file-row"
                        onClick={(e) => {
                          // Eğer bir butona tıklandıysa navigate etme
                          if (e.target.tagName === 'BUTTON' || e.target.closest('button')) {
                            return;
                          }
                          // Token kontrolü
                          const token = localStorage.getItem('token');
                          if (!token) {
                            alert('Oturum süreniz dolmuş. Lütfen tekrar giriş yapın.');
                            navigate('/login');
                            return;
                          }
                          navigate(`/case-files/${fileId}`);
                        }}
                      >
                        {visibleColumns.foyNo === true && <td>{file.FoyNo ?? file.foyNo ?? 'N/A'}</td>}
                        {visibleColumns.arsivNo === true && <td>{file.ArsivNo ?? file.arsivNo ?? 'N/A'}</td>}
                        {visibleColumns.muvekkil === true && <td>{file.Muvekkil ?? file.muvekkil ?? 'N/A'}</td>}
                        {visibleColumns.sorumlu === true && <td>{file.SorumluAdi ?? file.sorumluAdi ?? '-'}</td>}
                        {visibleColumns.karsiSigorta === true && <td>{file.KarsiTarafSigortaSirketi ?? file.karsiTarafSigortaSirketi ?? file.KarsiSigorta ?? file.karsiSigorta ?? '-'}</td>}
                        {visibleColumns.karsiTaraf1 === true && <td>{file.KarsiTaraf1 ?? file.karsiTaraf1 ?? '-'}</td>}
                        {visibleColumns.karsiTaraf2 === true && <td>{file.KarsiTaraf2 ?? file.karsiTaraf2 ?? '-'}</td>}
                        {visibleColumns.muvekkilTelefon === true && <td>{file.MuvekkilTelefon ?? file.muvekkilTelefon ?? '-'}</td>}
                        {visibleColumns.muvekkilIBAN === true && <td>{file.MuvekkilIBAN ?? file.muvekkilIBAN ?? '-'}</td>}
                        {visibleColumns.sigortaBasvuruTarihi === true && <td>{formatDate(file.SigortaBasvuruTarihi ?? file.sigortaBasvuruTarihi)}</td>}
                        {visibleColumns.davaAcilisTarihi === true && <td>{formatDate(file.DavaAcilisTarihi ?? file.davaAcilisTarihi)}</td>}
                        {visibleColumns.sozlesmeOrani === true && <td>{file.SozlesmeOrani ?? file.sozlesmeOrani ?? '-'}</td>}
                        {visibleColumns.mahkemeEsas === true && <td>{file.MahkemeEsas ?? file.mahkemeEsas ?? '-'}</td>}
                        {visibleColumns.muvekkilPlaka === true && <td>{file.MuvekkilPlaka ?? file.muvekkilPlaka ?? '-'}</td>}
                        {visibleColumns.karsiTarafPlaka === true && <td>{file.KarsiTarafPlaka ?? file.karsiTarafPlaka ?? '-'}</td>}
                        {visibleColumns.kazaSehri === true && <td>{file.KazaSehri ?? file.kazaSehri ?? '-'}</td>}
                        {visibleColumns.kazaTarihi === true && <td>{formatDate(file.KazaTarihi ?? file.kazaTarihi)}</td>}
                        {visibleColumns.dosyaGelisTarihi === true && <td>{formatDate(file.DosyaGelisTarihi ?? file.dosyaGelisTarihi)}</td>}
                        {visibleColumns.davaTuru === true && <td>{davaTuruEtiketi(file) || 'Seçiniz'}</td>}
                        {visibleColumns.tahkimBasvuruTarihi === true && <td>{formatDate(file.TahkimBasvuruTarihi ?? file.tahkimBasvuruTarihi)}</td>}
                        {visibleColumns.referans === true && <td>{file.Referans ?? file.referans ?? '-'}</td>}
                        {visibleColumns.raporTarihi === true && <td>{formatDate(file.RaporTarihi ?? file.raporTarihi)}</td>}
                        {visibleColumns.zamanAsimiTarihi === true && <td>{formatDate(file.ZamanAsimiTarihi ?? file.zamanAsimiTarihi)}</td>}
                        {visibleColumns.durusmaTarihi === true && <td>{file.DurusmaTarihi ?? file.durusmaTarihi ? formatDateForDisplay(file.DurusmaTarihi ?? file.durusmaTarihi) : '-'}</td>}
                        {visibleColumns.status === true && (
                          <td>
                            <span className={`status-badge status-${status}`}>
                              {CASE_FILE_STATUS_LABELS[status] ?? 'Bilinmiyor'}
                            </span>
                          </td>
                        )}
                        {visibleColumns.icraDosyaNo === true && <td>{file.IcraDosyaNo ?? file.icraDosyaNo ?? '-'}</td>}
                        {visibleColumns.savcilikCezaDosyaNo === true && <td>{file.SavcilikCezaDosyaNo ?? file.savcilikCezaDosyaNo ?? '-'}</td>}
                        {visibleColumns.mahkemaBilgisi === true && (
                          <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? ''}>
                            {file.MahkemaBilgisi ?? file.mahkemaBilgisi ?? '-'}
                          </td>
                        )}
                        {visibleColumns.sulhDurumu === true && <td>{file.SulhDurumu ?? file.sulhDurumu ?? '-'}</td>}
                        {visibleColumns.ibraAsamasi === true && <td>{file.IbraAsamasi ?? file.ibraAsamasi ?? '-'}</td>}
                        {visibleColumns.gelenOdeme === true && <td>{file.GelenOdemeDurumu ?? file.gelenOdemeDurumu ?? '-'}</td>}
                        {visibleColumns.gidenOdeme === true && <td>{file.GidenOdemeDurumu ?? file.gidenOdemeDurumu ?? '-'}</td>}
                        {visibleColumns.smm === true && <td>{file.SMM ?? file.smm ?? '-'}</td>}
                        {visibleColumns.karsiTarafVekili === true && <td>{file.KarsiTarafSigortaSirketiVekili ?? file.karsiTarafSigortaSirketiVekili ?? '-'}</td>}
                        {visibleColumns.sonIslemYapan === true && <td>{file.SonIslemYapanAdi ?? file.sonIslemYapanAdi ?? '-'}</td>}
                        {visibleColumns.createdDate === true && <td>{formatDate(file.CreatedDate ?? file.createdDate)}</td>}
                        {visibleColumns.updatedDate === true && <td>{formatDate(file.UpdatedDate ?? file.updatedDate)}</td>}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-info">
            <p>
              {filteredFiles.length} kayıttan {indexOfFirstItem + 1} - {Math.min(indexOfLastItem, filteredFiles.length)} arasındaki kayıtlar gösteriliyor
              {archivedFiles.length !== filteredFiles.length && ` (Toplam ${archivedFiles.length} kayıttan filtrelendi)`}
            </p>
          </div>

          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              ‹
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  className={`pagination-btn ${currentPage === pageNum ? 'active' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              );
            })}
            {totalPages > 5 && currentPage < totalPages - 2 && <span className="pagination-ellipsis">...</span>}
            {totalPages > 5 && (
              <button
                className={`pagination-btn ${currentPage === totalPages ? 'active' : ''}`}
                onClick={() => setCurrentPage(totalPages)}
              >
                {totalPages}
              </button>
            )}
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
            >
              ›
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ArchivedFiles;